import React from 'react';
import { useParams } from 'react-router-dom';
import { Button, Container, Alert } from 'reactstrap';
import { IoOpenOutline, IoRefreshOutline } from 'react-icons/io5';
import StandardLayout from '@/common/layouts/StandardLayout';
import DepositDetailCard from './components/DepositDetailCard';
import TransactionDetails from './components/TransactionDetails';
import UserInfo from './components/UserInfo';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getDepositByIdRest, retryDepositRest } from '@/services/deposits/deposits-service';
import { ReactComponent as TetherIcon } from '@/assets/tokens/tether-green.svg';
import { getBlockExplorerUrl } from '@/common/utils/block-explorer';
import { getStatusBadgeColor } from '@/pages/deposits/utils/get-status-badge-color';
import { formatCurrency } from '@/common/utils/formatCurrency';
import { getDepositStatus } from '../utils/get-deposits-status';

const DepositDetailsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const {
    data: deposit,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['deposit', id],
    queryFn: () => getDepositByIdRest(id!),
    enabled: !!id,
  });

  const retryMutation = useMutation({
    mutationFn: retryDepositRest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deposit', id] });
      refetch();
    },
  });

  const canRetry =
    deposit?.inboundTx?.status === 'CONFIRMED' &&
    (!deposit?.outboundTx || deposit?.outboundTx?.status === 'FAILED' || deposit?.outboundTx?.status === 'PENDING');

  if (error) {
    return (
      <StandardLayout pageTitle="Deposit Details" showBackButton>
        <Container fluid>
          <Alert color="danger">Error loading deposit details</Alert>
        </Container>
      </StandardLayout>
    );
  }

  if (isLoading || !deposit) {
    return (
      <StandardLayout pageTitle="Deposit Details" showBackButton>
        <Container fluid>
          <div className="d-flex justify-content-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </StandardLayout>
    );
  }

  return (
    <StandardLayout
      pageTitle={`Deposit #${id}`}
      showBackButton
      headerButton={
        deposit.inboundTx && (
          <Button
            className="d-flex align-items-center justify-content-center gap-2"
            color="primary"
            onClick={() => window.open(getBlockExplorerUrl(deposit.chain, deposit.inboundTx.txId), '_blank')}
          >
            View on Explorer
            <IoOpenOutline className="mb-0 text-xl" size={20} />
          </Button>
        ) &&
        canRetry ? (
          <Button
            className="d-flex align-items-center justify-content-center gap-2"
            color="warning"
            onClick={() => retryMutation.mutate(id!)}
            disabled={retryMutation.isPending}
          >
            Retry Deposit
            <IoRefreshOutline className="mb-0 text-xl" size={20} />
          </Button>
        ) : null
      }
    >
      <Container fluid className="mt-4">
        <div className="w-100 mb-4">
          <div className="row g-3">
            <div className="col-xl-3 col-sm-6">
              <DepositDetailCard title="Status">
                <h4
                  className={`fs-4 badge mb-0 p-0 text-start ${getStatusBadgeColor(getDepositStatus(deposit.inboundTx, deposit.outboundTx))}`}
                >
                  {getDepositStatus(deposit.inboundTx, deposit.outboundTx)}
                </h4>
              </DepositDetailCard>
            </div>

            <div className="col-xl-3 col-sm-6">
              <DepositDetailCard title="Amount">
                <div className="d-flex align-items-center justify-content-start gap-2">
                  <h4 className="mb-0 text-lg">{formatCurrency(deposit.amount)}</h4>
                  <TetherIcon className="mb-0 text-xl" style={{ width: '24px', height: '24px' }} />
                </div>
              </DepositDetailCard>
            </div>

            <div className="col-xl-3 col-sm-6">
              <DepositDetailCard title="Chain">
                <h4 className="mb-0 text-lg">{deposit.chain}</h4>
              </DepositDetailCard>
            </div>

            <div className="col-xl-3 col-sm-6">
              <DepositDetailCard title="Created At">
                <h4 className="mb-0 text-lg">{new Date(deposit.createdAt).toLocaleString()}</h4>
              </DepositDetailCard>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-6">
            {deposit.inboundTx && (
              <TransactionDetails transaction={deposit.inboundTx} title="Inbound Transaction" chain={deposit.chain} />
            )}
          </div>
          <div className="col-lg-6">
            {deposit.outboundTx && (
              <TransactionDetails
                transaction={deposit.outboundTx}
                title="Outbound Transaction"
                chain={deposit.chain}
                isOutbound={true}
              />
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <UserInfo user={deposit.user} />
          </div>
        </div>
      </Container>
    </StandardLayout>
  );
};

export default DepositDetailsPage;
